/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Key,
  Plus,
  DollarSign,
  Calendar,
  MoreHorizontal,
  Trash2,
  Eye,
  Send,
  Loader2,
  Edit,
} from 'lucide-react';
import { Button } from '../ui/button';
import { AddRentalModal } from '../rental/AddRentalModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { actions } from '../../actions';
import { services } from '../../services';
import { sendTransactionHash } from '../../services/transactionApi';
import { createAdminNotification } from '../../services/notificationService';
import { Web3Data } from '../../types/nft/create.rental';
import { NFTRecord } from '../../types/nft/rental';
import getContractAddresses from '../../contractData/contractAddress/addresses';
import { web3 } from '../../web3';
import { Web3Error } from 'web3';

interface Rental {
  id: string;
  _id: string;
  property: string;
  unit: string;
  tenant: string;
  monthlyRent: number;
  status:
    | 'ACTIVE'
    | 'LATE'
    | 'PENDING'
    | 'COLLECTED'
    | 'AVAILABLE'
    | 'RENTED'
    | 'EXPIRED'
    | 'CANCELLED';
  nextPayment: string;
  collected: boolean;
  rentPaid: boolean;
  assetAddress: string;
  listingId: number;
  totalRentalPrice: string;
  allowPayRent: boolean;
  endDate?: string;
  tenantId?: any;
}

const RentalList = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusUpdateId, setStatusUpdateId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [rentalContractInstance, setRentalContractInstance] = useState<any>(null);
  const [tokenContract, setTokenContract] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Initialize Web3 and contracts
  useEffect(() => {
    const initializeWeb3 = async () => {
      try {
        const web3Data = await services.getWeb3();
        if (web3Data) {
          setWeb3Data(web3Data as Web3Data);
        }
        // eslint-disable-next-line no-empty
      } catch (error) {
        console.error('Error initializing Web3:', error);
        // Web3 initialization failed, will retry or show error state
        return; // Early return on initialization error
      }
    };

    const initializeContract = async () => {
      try {
        const instance = await actions.getRentalPoolContract();
        setRentalContractInstance(instance);
        // eslint-disable-next-line no-empty
      } catch (error) {
        console.error('Error initializing Web3:', error);
        // Web3 initialization failed, will retry or show error state
        return; // Early return on initialization error
      }
    };

    const initializeTokenContract = async () => {
      try {
        const tokenInstance = await actions.getTokenContract();
        setTokenContract(tokenInstance);
        // eslint-disable-next-line no-empty
      } catch (error) {
        console.error('Error initializing Web3:', error);
        // Web3 initialization failed, will retry or show error state
        return; // Early return on initialization error
      }
    };

    initializeWeb3();
    initializeContract();
    initializeTokenContract();
  }, []);

  const fetchRentals = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/rental/getRentalList?page=${page}&limit=${perPage}`
      );
      const result = await response.json();

      if (result.data && Array.isArray(result.data)) {
        // Map API data to Rental interface
        const mappedRentals: Rental[] = result.data.map((record: NFTRecord) => {
          // Determine status based on API data
          let status: Rental['status'] = 'ACTIVE';
          if (record.rentPaid) {
            status = 'COLLECTED';
          } else if (record.status === 'RENTED') {
            status = 'ACTIVE';
          } else if (record.status === 'AVAILABLE') {
            status = 'PENDING';
          } else if (record.status === 'EXPIRED') {
            status = 'LATE';
          } else if (record.status) {
            status = record.status as Rental['status'];
          }

          // Extract unit from rental description if available, otherwise try to parse from asset name
          let unit = 'N/A';
          if (record.rentalDescription) {
            // Try to extract unit from description (e.g., "Unit/Floor: Unit 4A" or "Unit 4A, Floor 12")
            const unitMatch = record.rentalDescription.match(/(?:Unit|Floor)[:\s]+([^\n,]+)/i);
            if (unitMatch && unitMatch[1]) {
              unit = unitMatch[1].trim();
            }
          }
          // Fallback: try to extract from asset name if description doesn't have it
          if (unit === 'N/A' && record.assetId?.assetName) {
            const nameParts = record.assetId.assetName.split(' ');
            const lastPart = nameParts[nameParts.length - 1];
            // Check if last part looks like a unit (contains numbers or letters like "4A", "12", etc.)
            if (lastPart && /[0-9A-Za-z]/.test(lastPart)) {
              unit = lastPart;
            }
          }

          // Get tenant name
          const tenant = record.tenantId?.name || record.tenantId?.username || 'No Tenant';

          // Calculate next payment date
          let nextPayment: string;
          // Format as YYYY-MM-DD in the VIEWER's local timezone ('en-CA' yields that
          // ISO-like format) instead of toISOString(), which would print the UTC day
          // and can be off by one near midnight for non-UTC users.
          if (record.endDate) {
            // Use endDate from rental record
            nextPayment = new Date(record.endDate).toLocaleDateString('en-CA');
          } else if (record.startDate && record.rentalDurationInDays) {
            // Calculate from startDate + duration
            const start = new Date(record.startDate);
            const end = new Date(start);
            end.setDate(end.getDate() + record.rentalDurationInDays);
            nextPayment = end.toLocaleDateString('en-CA');
          } else if (record.assetId?.saleEndDate) {
            // Fallback to asset saleEndDate
            nextPayment = new Date(record.assetId.saleEndDate).toLocaleDateString('en-CA');
          } else {
            // Last resort: use current date + 30 days as estimate
            const estimate = new Date();
            estimate.setDate(estimate.getDate() + 30);
            nextPayment = estimate.toLocaleDateString('en-CA');
          }

          return {
            id: record._id || record.id,
            _id: record._id || record.id,
            property: record.assetId?.assetName || 'Unknown Property',
            unit: unit,
            tenant: tenant,
            monthlyRent: parseFloat(record.totalRentalPrice) || 0,
            status: status,
            nextPayment: nextPayment,
            collected: record.rentPaid || false,
            rentPaid: record.rentPaid || false,
            assetAddress: record.assetAddress,
            listingId: record.listingId,
            totalRentalPrice: record.totalRentalPrice,
            allowPayRent: record.allowPayRent,
            endDate: record.assetId?.saleEndDate,
            tenantId: record.tenantId,
          };
        });

        setRentals(mappedRentals);
        setTotalPages(result.totalPages || 1);
        setTotalRecords(result.totalRecords || 0);
      }
    } catch (error) {
      toast({
        description: 'Failed to load rentals',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchRentals();
  }, [page, perPage]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleAddRental = () => {
    // Refresh the list after adding
    fetchRentals();
  };

  const handleDeleteRental = async (id: string) => {
    try {
      // Note: The delete endpoint might need to be updated based on your backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/rentals/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setRentals(rentals.filter((r) => r.id !== id));
        toast({
          description: 'Rental removed successfully',
          variant: 'default',
        });
        createAdminNotification({
          type: 'transaction',
          title: 'Rental removed',
          message: 'A rental listing was removed.',
          priority: 'low',
        });
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast({
        description: 'Failed to delete rental',
        variant: 'destructive',
      });
    } finally {
      setShowDeletePopup(false);
      setDeleteId(null);
      setDeleteName(null);
    }
  };

  const handleMarkCollected = async (rental: Rental) => {
    // This would typically trigger the pay rent functionality
    if (!rental.allowPayRent && !rental.rentPaid) {
      await handlePayRent(rental.assetAddress, rental.listingId, rental.monthlyRent);
    } else {
      toast({
        description: rental.rentPaid ? 'Rent already paid' : 'Tenant is responsible for payment',
        variant: 'default',
      });
    }
  };

  const handleUpdateStatus = (rental: Rental) => {
    setStatusUpdateId(rental._id);
    // Map frontend status to backend status
    let backendStatus = 'AVAILABLE';
    if (rental.status === 'ACTIVE' || rental.status === 'RENTED') {
      backendStatus = 'RENTED';
    } else if (rental.status === 'LATE' || rental.status === 'EXPIRED') {
      backendStatus = 'EXPIRED';
    } else if (rental.status === 'AVAILABLE' || rental.status === 'PENDING') {
      backendStatus = 'AVAILABLE';
    } else if (rental.status === 'CANCELLED') {
      backendStatus = 'CANCELLED';
    }
    setNewStatus(backendStatus);
    setShowStatusDialog(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdateId || !newStatus) return;

    setUpdatingStatus(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/rental/${statusUpdateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast({
          description: 'Rental status updated successfully',
          variant: 'default',
        });
        createAdminNotification({
          type: 'transaction',
          title: 'Rental status updated',
          message: 'Rental listing status was updated.',
          priority: 'low',
        });
        setShowStatusDialog(false);
        setStatusUpdateId(null);
        setNewStatus('');
        fetchRentals();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update status');
      }
    } catch (error: any) {
      toast({
        description: error.message || 'Failed to update rental status',
        variant: 'destructive',
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePayRent = async (
    assetAddress: string,
    listingId: number,
    totalRentalPrice: number
  ) => {
    if (!web3Data || !rentalContractInstance || !tokenContract) {
      toast({
        description: 'Web3 not initialized. Please connect your wallet.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { rentalPool } = getContractAddresses();

      const owner = await rentalContractInstance.methods.owner().call();

      if (owner !== web3Data.accounts[0]) {
        toast({
          description: "You haven't logged in with the owner account.",
          variant: 'destructive',
        });
        return;
      }

      const amountInWei = web3.utils.toWei((totalRentalPrice || 0).toString(), 'ether');

      const getAllowance = await tokenContract.methods
        .allowance(web3Data.accounts[0], rentalPool)
        .call();

      if (Number(getAllowance) === 0 || Number(getAllowance) < Number(amountInWei)) {
        const maxUint256 =
          '115792089237316195423570985008687907853269984665640564039457584007913129639935';
        await tokenContract.methods
          .approve(rentalPool, maxUint256)
          .send({ from: web3Data.accounts[0] })
          .on('transactionHash', () => {
            toast({
              description: 'Approving tokens...',
              variant: 'default',
            });
          })
          .on('receipt', () => {
            toast({
              description: 'Token approval successful',
              variant: 'default',
            });
          })
          .on('error', (error: Web3Error) => {
            toast({
              description: 'Token approval failed',
              variant: 'destructive',
            });
            throw error;
          });
      }

      await rentalContractInstance.methods
        .payRent(assetAddress, listingId)
        .send({ from: web3Data?.accounts[0] })
        .on('transactionHash', (hash: any) => {
          sendTransactionHash(hash).catch((apiError) => {});
        })
        .on('receipt', (receipt: any) => {
          toast({
            description: 'Rent payment successful!',
            variant: 'default',
          });
          createAdminNotification({
            type: 'transaction',
            title: 'Rent paid',
            message: 'Rent payment was completed successfully.',
            priority: 'medium',
          });
          // Refresh the data after successful payment
          fetchRentals();
        })
        .on('error', (error: any) => {
          toast({
            description: 'Error paying rent. Please try again.',
            variant: 'destructive',
          });
        });
    } catch (error) {
      toast({
        description: 'Admin denied the transaction.',
        variant: 'destructive',
      });
    }
  };

  // Calculate summary statistics
  const totalMonthlyRevenue = rentals.reduce((sum, r) => sum + r.monthlyRent, 0);
  const collectedRevenue = rentals
    .filter((r) => r.collected)
    .reduce((sum, r) => sum + r.monthlyRent, 0);
  const activeRentals = rentals.filter(
    (r) => r.status === 'ACTIVE' || r.status === 'COLLECTED' || r.status === 'RENTED'
  ).length;
  const latePayments = rentals.filter((r) => r.status === 'LATE' || r.status === 'EXPIRED').length;

  if (loading && rentals.length === 0) {
    return (
      <div className='w-full'>
        <div className='flex items-center justify-center min-h-[400px] rounded-lg border bg-white border-gray-200 dark:bg-[#0b222c] dark:border-[#095D52]'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-600 dark:text-[#e9e6c9]' />
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Rentals</h1>
          <p className='text-muted-foreground'>Track rental income and manage rental properties.</p>
        </div>
        <div className='flex gap-3'>
          <Button className='gap-2' onClick={() => setIsAddModalOpen(true)}>
            <Plus className='w-4 h-4' />
            Add Rental
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <div className='bg-muted/30 border border-border rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'>
              <DollarSign className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Monthly Revenue</p>
              <p className='text-2xl font-bold text-foreground'>
                ${totalMonthlyRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className='bg-muted/30 border border-border rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-primary/20 text-primary'>
              <Send className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Ready to Distribute</p>
              <p className='text-2xl font-bold text-foreground'>
                ${collectedRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className='bg-muted/30 border border-border rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'>
              <Key className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Active Rentals</p>
              <p className='text-2xl font-bold text-foreground'>{activeRentals}</p>
            </div>
          </div>
        </div>
        <div className='bg-muted/30 border border-border rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'>
              <Calendar className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Late Payments</p>
              <p className='text-2xl font-bold text-foreground'>{latePayments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rentals Table */}
      <div className='bg-muted/30 border border-border rounded-xl overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead>
              <tr className='bg-muted/30 border-b border-border'>
                <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  PROPERTY
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  UNIT
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  TENANT
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  MONTHLY RENT
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  STATUS
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  NEXT PAYMENT
                </th>
                <th className='px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {rentals.length === 0 ? (
                <tr>
                  <td colSpan={7} className='px-4 py-8 text-center text-muted-foreground'>
                    No rentals found
                  </td>
                </tr>
              ) : (
                rentals.map((rental) => (
                  <tr
                    key={rental.id}
                    className={`border-b border-border hover:bg-muted/20 transition-colors ${
                      rental.collected ? 'bg-primary/5' : ''
                    }`}
                  >
                    <td className='px-4 py-3 font-medium text-foreground'>{rental.property}</td>
                    <td className='px-4 py-3 text-muted-foreground'>{rental.unit}</td>
                    <td className='px-4 py-3 text-foreground'>{rental.tenant}</td>
                    <td className='px-4 py-3 font-mono text-green-600 dark:text-green-400 font-semibold'>
                      ${rental.monthlyRent.toLocaleString()}
                    </td>
                    <td className='px-4 py-3'>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${
                          rental.status === 'COLLECTED' || rental.rentPaid
                            ? 'bg-primary/20 text-primary border-primary/30'
                            : rental.status === 'ACTIVE' || rental.status === 'RENTED'
                              ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-300 dark:border-green-600'
                              : rental.status === 'LATE' || rental.status === 'EXPIRED'
                                ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-600'
                                : 'bg-muted text-muted-foreground border-border'
                        }`}
                      >
                        {rental.status}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-muted-foreground'>
                      {rental.nextPayment
                        ? new Date(rental.nextPayment).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className='px-4 py-3 text-right'>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='ghost' size='icon' className='h-8 w-8'>
                            <MoreHorizontal className='w-4 h-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate(`/rental/${rental._id}`)}>
                            <Eye className='w-4 h-4 mr-2' />
                            View Details
                          </DropdownMenuItem>
                          {/* {!rental.collected && !rental.allowPayRent && (
                            <DropdownMenuItem onClick={() => handleMarkCollected(rental)}>
                              <DollarSign className='w-4 h-4 mr-2' />
                              Pay Rent
                            </DropdownMenuItem>
                          )}
                          {!rental.collected && rental.allowPayRent && (
                            <DropdownMenuItem disabled>
                              <DollarSign className='w-4 h-4 mr-2' />
                              Tenant Payment
                            </DropdownMenuItem>
                          )} */}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleUpdateStatus(rental)}>
                            <Edit className='w-4 h-4 mr-2' />
                            Edit Rental
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className='text-destructive'
                            onClick={() => {
                              setDeleteId(rental.id);
                              setDeleteName(rental.property);
                              setShowDeletePopup(true);
                            }}
                          >
                            <Trash2 className='w-4 h-4 mr-2' />
                            Remove Rental
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex flex-col sm:flex-row items-center justify-between gap-4'>
          <div className='text-sm text-muted-foreground'>
            Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, totalRecords)} of{' '}
            {totalRecords} rentals
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <div className='text-sm font-medium'>
              Page {page} of {totalPages}
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
          <div className='p-4 sm:p-6 rounded-lg shadow-lg max-w-sm w-full mx-4 bg-white border border-gray-200 dark:bg-[#0b222c] dark:border-[#095D52]'>
            <h3 className='text-lg font-semibold mb-4 text-gray-900 dark:text-[#e9e6c9]'>
              Delete Rental - {deleteName}
            </h3>
            <p className='mb-6 text-sm sm:text-base text-gray-600 dark:text-[#e9e6c9]'>
              Are you sure you want to delete this rental?
            </p>
            <div className='flex flex-col sm:flex-row justify-end gap-3'>
              <Button
                variant='outline'
                onClick={() => {
                  setShowDeletePopup(false);
                  setDeleteId(null);
                  setDeleteName(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={() => deleteId && handleDeleteRental(deleteId)}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Update Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Rental Status</DialogTitle>
            <DialogDescription>Select a new status for this rental property.</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='status'>Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder='Select status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='AVAILABLE'>Available</SelectItem>
                  <SelectItem value='RENTED'>Rented</SelectItem>
                  <SelectItem value='EXPIRED'>Expired</SelectItem>
                  <SelectItem value='CANCELLED'>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setShowStatusDialog(false);
                setStatusUpdateId(null);
                setNewStatus('');
              }}
              disabled={updatingStatus}
            >
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={updatingStatus || !newStatus}>
              {updatingStatus ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modals */}
      <AddRentalModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onAdd={handleAddRental}
      />
    </div>
  );
};

export default RentalList;
